// The plugin no longer writes markup inside a frame, it only tags it and describes the enrichment in
// pagesData[].enrichments. The reader builds the element at run time, the editor reads elements, so
// they are built here from the same entry and everything after this point works as before.

import constants from './constants';
import { getTooltipAction, TooltipAction } from '../helpers';

const ENRICHMENT_CLASS = 'webPublication-html-enrichment';
const SERVER_ENRICHMENT_CLASS = 'webpublication-enrichment'; // the class the server adds to the anchor
const PLUGIN_EVENT_NAME = 'WebPublicationAction';
const EVENT_PROPERTY = ',"event":typeof(event) !== "undefined" ? event : undefined';
const MEDIA_PARAM = '${media}';
const ENRICHMENT_TOKEN_ATTRIBUTE = 'data-enrichment-token';
const YOUTUBE_ATTRIBUTES = '?enablejsapi=1&rel=0';

enum MediaEnrichmentType {
	video = 'video',
	animation = 'animation',
}

enum DriveItemType {
	movie = 'MOVIE',
	component = 'COMPONENT',
	externalLink = 'EXTERNAL_LINK',
}

interface DriveItem {
	label?: string;
	relUrl?: string;
	externalRelUrl?: string;
	isExternalLink?: boolean;
	type?: string;
}

export interface EnrichmentParams {
	assetDriveItem?: DriveItem;
	defaultAssetDriveItem?: DriveItem; // what the designer chose, written by the editor on the first change
	param?: string; // what the reader opens, kept in step with assetDriveItem
	settings?: { [key: string]: boolean };
	tooltip?: string;
	isTooltipActive?: boolean;
}

export interface EnrichmentEntry {
	type: string;
	className?: string;
	params?: EnrichmentParams; // absent when the plugin had no parameters to write
}

export interface EnrichmentsConfig {
	[token: string]: EnrichmentEntry;
}

const hasMediaParam = (url: string): boolean => /\$media\$|\$\{media\}|\$%7Bmedia%7D/.test(url);

const isYoutubeUrl = (url: string): boolean => url.includes('youtube.com') || url.includes('youtu.be');

// the same url the plugin builds, the server resolves ${media} when it serves the publication
const getDriveMediaUrl = (relUrl: string, isAnimation: boolean): string => {
	if (hasMediaParam(relUrl)) {
		return relUrl;
	}

	const parts = relUrl.split('/');
	const endsWithSlash = relUrl[relUrl.length - 1] === '/';
	const idWithExtension = endsWithSlash ? parts[parts.length - 2] : parts[parts.length - 1];

	return `${MEDIA_PARAM}${idWithExtension}${isAnimation ? '/index.html' : ''}`;
};

// the same cover url the plugin builds, the editor derives it again from the source
const getVideoCover = (url: string): string => {
	const parts = url.split('.');
	parts.pop();

	return `${parts.join('.')}/cover.jpg`;
};

const getYoutubeSrc = (url: string, settings?: { [key: string]: boolean }): string => {
	if (!settings) {
		return url + YOUTUBE_ATTRIBUTES;
	}

	return Object.keys(settings).reduce((src, key) => {
		const name = key === 'muted' ? 'mute' : key;

		return `${src}&${name}=${settings[key] ? 1 : 0}`;
	}, url + YOUTUBE_ATTRIBUTES);
};

const getClassName = (className?: string): string => (
	className ? `${ENRICHMENT_CLASS} ${className}` : ENRICHMENT_CLASS
);

const setTooltip = (element: HTMLElement, params: EnrichmentParams): void => {
	element.setAttribute('onmouseenter', getTooltipAction(TooltipAction.SHOW, params.tooltip || ''));
	element.setAttribute('onmouseleave', getTooltipAction(TooltipAction.HIDE));
};

const createVideo = (htmlDocument: Document, src: string, entry: EnrichmentEntry): HTMLElement => {
	const params = entry.params || {};
	const settings = params.settings;
	const video = htmlDocument.createElement('video');

	video.className = getClassName(entry.className);
	video.setAttribute('disablePictureInPicture', '');
	video.setAttribute('playsinline', '');

	if (settings) {
		Object.keys(settings).forEach((key) => {
			if (settings[key]) {
				video.setAttribute(key, '');
			}
		});
	} else {
		video.setAttribute('controls', '');
	}

	video.setAttribute('width', '100%');
	video.setAttribute('height', '100%');
	video.setAttribute('data-label', (params.assetDriveItem && params.assetDriveItem.label) || '');
	video.setAttribute('poster', getVideoCover(src));
	video.setAttribute('controlslist', 'nodownload');

	const source = htmlDocument.createElement('source');
	source.setAttribute('src', src);
	source.setAttribute('type', 'video/mp4');
	video.appendChild(source);

	return video;
};

const createIframe = (htmlDocument: Document, src: string, entry: EnrichmentEntry, isYoutube: boolean): HTMLElement => {
	const iframe = htmlDocument.createElement('iframe');

	iframe.className = getClassName(entry.className);
	iframe.setAttribute('width', '100%');
	iframe.setAttribute('height', '100%');
	iframe.setAttribute('src', src);
	iframe.setAttribute('frameborder', '0');

	if (isYoutube) {
		iframe.setAttribute('allow', 'autoplay; encrypted-media');
		iframe.setAttribute('allowfullscreen', 'allowfullscreen');
	}

	return iframe;
};

// the asset decides what is built, not the type chosen in the plugin panel
const createMediaElement = (htmlDocument: Document, entry: EnrichmentEntry): HTMLElement | null => {
	const params = entry.params || {};
	const driveItem = params.assetDriveItem;

	if (!driveItem) {
		return null;
	}

	const isExternal = !!driveItem.isExternalLink || driveItem.type === DriveItemType.externalLink;
	const relUrl = driveItem.relUrl || '';

	if (isExternal) {
		// only the external url counts here, relUrl is the fallback of the old youtube links
		const url = driveItem.externalRelUrl || relUrl;

		return isYoutubeUrl(url)
			? createIframe(htmlDocument, getYoutubeSrc(url, params.settings), entry, true)
			: createIframe(htmlDocument, url, entry, false);
	}

	// a replaced resource keeps the external url of the old one, so never read it here
	if (driveItem.type === DriveItemType.movie) {
		return createVideo(htmlDocument, getDriveMediaUrl(relUrl, false), entry);
	}

	if (driveItem.type === DriveItemType.component) {
		return createIframe(htmlDocument, getDriveMediaUrl(relUrl, true), entry, false);
	}

	return null;
};

// every other type was an anchor covering the frame, the server put the dispatch in onclick
const createActionElement = (htmlDocument: Document, entry: EnrichmentEntry): HTMLElement => {
	// the editor keeps the designer's choice here, the reader has no use for it
	const { defaultAssetDriveItem, ...params } = entry.params || ({} as EnrichmentParams);
	const detail = `{"type":"${entry.type}","params":${JSON.stringify(params)}${EVENT_PROPERTY}}`;
	const anchor = htmlDocument.createElement('a');

	anchor.className = `${ENRICHMENT_CLASS} ${SERVER_ENRICHMENT_CLASS}`;
	anchor.setAttribute('draggable', 'false');
	anchor.setAttribute('href', 'javascript:void(0)');
	anchor.setAttribute(
		'onclick',
		`javascript:document.dispatchEvent(new CustomEvent("${PLUGIN_EVENT_NAME}", {"detail":${detail}})); return false;`
	);

	const tooltipArea = htmlDocument.createElement('div');
	tooltipArea.setAttribute('style', 'width: 100%;height: 100%;');
	anchor.appendChild(tooltipArea);

	return anchor;
};

// the substring is for a token that arrived glued to another one, but a digit right after it means a
// longer token only starts the same way
const findByClass = (root: Document | Element, className: string): Element | null => {
	const exact = root.querySelector(`[class~="${className}"]`);

	if (exact) {
		return exact;
	}

	return [...root.querySelectorAll(`[class*="${className}"]`)].find((element) => {
		const classes = element.getAttribute('class') || '';

		return classes.split(className).slice(1).some(rest => !/^\d/.test(rest));
	}) || null;
};

const isAlreadyBuilt = (frame: Element, entry: EnrichmentEntry): boolean => (
	!!frame.querySelector(`[class*="${ENRICHMENT_CLASS}"]`) ||
	(!!entry.className && !!findByClass(frame, entry.className))
);

// the markup goes inside the frame the way the export used to nest it
const buildEnrichmentElement = (htmlDocument: Document, frame: Element, entry: EnrichmentEntry, token: string): void => {
	if (isAlreadyBuilt(frame, entry)) {
		return;
	}

	const isMedia = entry.type === MediaEnrichmentType.video || entry.type === MediaEnrichmentType.animation;
	const element = isMedia ? createMediaElement(htmlDocument, entry) : createActionElement(htmlDocument, entry);

	if (!element) {
		return;
	}

	setTooltip(element, entry.params || {});

	// removeEditableElements and the locked cover image both look for this wrapper
	const wrapper = htmlDocument.createElement('div');
	wrapper.className = constants.videoOrIframeWrapperId;
	// the entry this came from, so the save does not have to work it out again
	wrapper.setAttribute(ENRICHMENT_TOKEN_ATTRIBUTE, token);
	const object = htmlDocument.createElement('object');
	object.appendChild(element);
	wrapper.appendChild(object);
	frame.appendChild(wrapper);
};

export const findEnrichmentToken = (container: Element): string | null => {
	const built = container.querySelector(`[${ENRICHMENT_TOKEN_ATTRIBUTE}]`);

	return built ? built.getAttribute(ENRICHMENT_TOKEN_ATTRIBUTE) : null;
};

export default (htmlDocument: Document, enrichments?: EnrichmentsConfig): Document => {
	if (!enrichments) {
		return htmlDocument;
	}

	Object.keys(enrichments).forEach((token) => {
		const frame = findByClass(htmlDocument, token);

		if (frame) {
			buildEnrichmentElement(htmlDocument, frame, enrichments[token], token);
		}
	});

	return htmlDocument;
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/buildEnrichmentElements.ts